/**
 * Demo Mode Manager
 * Manages demo mode state and installs Axios interceptors that return
 * simulated data instead of making real API calls.
 *
 * Uses a custom adapter approach: when demo mode is on, the request interceptor
 * replaces the Axios adapter with one that immediately resolves with mock data.
 */
import mainApiClient from './interceptor.mainApiClient';
import mlFunctionsClient from './interceptor.mlFunctionsClient';
import * as demo from './demoData.service';

const STORAGE_KEY = 'safepath_demo_mode';

// Track interceptor IDs for removal
let mainInterceptorId = null;
let mlInterceptorId = null;

// ============================================================================
// PUBLIC API
// ============================================================================

export function isDemoMode() {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

export function setDemoMode(enabled) {
  localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  if (enabled) {
    installDemoInterceptors();
  } else {
    removeDemoInterceptors();
  }
}

export function installDemoInterceptors() {
  // Remove existing first to avoid duplicates
  removeDemoInterceptors();

  // Main API interceptor — replaces adapter to return mock data
  mainInterceptorId = mainApiClient.interceptors.request.use((config) => {
    if (!isDemoMode()) return config;
    const mockData = matchMainApiUrl(config.url, config);
    if (mockData !== undefined) {
      // Replace the adapter so the real HTTP request never fires.
      // Axios 1.x adapter receives (config) and must return a Promise<AxiosResponse>.
      config.adapter = function demoAdapter(cfg) {
        return Promise.resolve({
          data: mockData,
          status: 200,
          statusText: 'OK (Demo)',
          headers: { 'content-type': 'application/json' },
          config: cfg,
          request: {},
        });
      };
    }
    return config;
  });

  // ML Functions interceptor — same pattern
  mlInterceptorId = mlFunctionsClient.interceptors.request.use((config) => {
    if (!isDemoMode()) return config;
    const mockData = matchMlFunctionsUrl(config.url, config);
    if (mockData !== undefined) {
      config.adapter = function demoAdapter(cfg) {
        return Promise.resolve({
          data: mockData,
          status: 200,
          statusText: 'OK (Demo)',
          headers: { 'content-type': 'application/json' },
          config: cfg,
          request: {},
        });
      };
    }
    return config;
  });

  console.log('[DemoMode] Interceptors installed on mainApiClient and mlFunctionsClient');
}

export function removeDemoInterceptors() {
  if (mainInterceptorId !== null) {
    mainApiClient.interceptors.request.eject(mainInterceptorId);
    mainInterceptorId = null;
  }
  if (mlInterceptorId !== null) {
    mlFunctionsClient.interceptors.request.eject(mlInterceptorId);
    mlInterceptorId = null;
  }
}

// ============================================================================
// URL PATTERN MATCHING
// ============================================================================

function extractId(url) {
  // Extract numeric ID from URLs like /api/aliens/123/... or ?person_id=123
  const pathMatch = url.match(/\/aliens\/(\d+)/);
  if (pathMatch) return pathMatch[1];

  const paramMatch = url.match(/person_id=(\d+)/);
  if (paramMatch) return paramMatch[1];

  const clusterMatch = url.match(/\/assignment\/(\d+)/);
  if (clusterMatch) return clusterMatch[1];

  const communityMatch = url.match(/community_id=(\d+)/);
  if (communityMatch) return communityMatch[1];

  // Extract person ID from /people/{id}/info
  const peopleMatch = url.match(/\/people\/(\d+)/);
  if (peopleMatch) return peopleMatch[1];

  return '999'; // fallback demo ID
}

function matchMainApiUrl(url, config) {
  if (!url) return undefined;
  const id = extractId(url);
  const method = (config.method || 'get').toLowerCase();

  // Order matters — more specific patterns first

  // ML Risk Score
  if (url.includes('/ml-risk-score/factors')) return demo.getDemoRiskFactorExplanation(id);
  if (url.includes('/ml-risk-score/history')) return demo.getDemoRiskScoreHistory(id);
  if (url.includes('/ml-risk-score')) return demo.getDemoRiskScore(id);

  // Anomaly endpoints — detect (POST) and train (POST) BEFORE generic pattern-of-life POST
  if (url.includes('/anomaly/detect')) return demo.getDemoDetectAnomaly(id);
  if (url.includes('/anomaly/train')) return demo.getDemoTrainEnsemble(id);
  if (url.includes('/anomaly/history')) return demo.getDemoAnomalyHistory(id);
  if (url.includes('/anomaly') && method !== 'post') return demo.getDemoDetectAnomaly(id);

  // Pattern of Life
  if (url.includes('/pattern-of-life/build') || url.includes('/pattern-of-life/rebuild') ||
      (url.includes('/pattern-of-life') && method === 'post')) {
    return demo.getDemoBuildPOL(id);
  }
  if (url.includes('/pattern-of-life/deviation')) return demo.getDemoDeviation(id);
  if (url.includes('/pattern-of-life')) return demo.getDemoPOLModel(id);

  // Pattern Clustering — specific patterns before catch-all
  if (url.includes('/patternclustering/assignment')) return demo.getDemoClusterAssignment(id);
  if (url.includes('/patternclustering/features')) return demo.getDemoBehavioralFeatures(id);
  if (url.includes('/patternclustering/visualization')) return demo.getDemoClusterVisualization(id);
  if (url.includes('/patternclustering/profiles')) return demo.getDemoClusterProfiles();
  if (url.includes('/patternclustering/profile/')) return demo.getDemoClusterProfiles()[0];
  if (url.includes('/patternclustering/train') && method === 'post') return demo.getDemoClusterTrainResult();
  if (url.includes('/patternclustering/cluster') && method === 'post') return demo.getDemoClusterTrainResult();
  if (url.includes('/patternclustering')) return demo.getDemoClusterAssignment(id);

  // Heatmap (mainApiClient) — specific before generic
  if (url.includes('/heatmap/temporal/generate')) return demo.getDemoTemporalHeatmap(id);
  if (url.includes('/heatmap/temporal')) return demo.getDemoTemporalHeatmap(id);
  if (url.includes('/heatmap/generate')) return demo.getDemoBehavioralHeatmap(id);
  if (url.includes('/heatmap')) return demo.getDemoBehavioralHeatmap(id);

  // Zone visits (mainApiClient — /api/aliens/{id}/zone-visits/...)
  if (url.includes('/zone-visits/stats')) return demo.getDemoZoneVisitStats(id);
  if (url.includes('/zone-visits/exposure-score')) {
    const stats = demo.getDemoZoneVisitStats(id);
    return { alienId: Number(id), exposureScore: stats.zoneExposureScore, days: 30 };
  }
  if (url.includes('/zone-visits')) return demo.getDemoZoneVisitHistory(id);

  // High-risk zones (mainApiClient)
  if (url.includes('/high-risk-zones/check')) return { inZone: false, zones: [] };
  if (url.includes('/high-risk-zones') && url.includes('/visits')) return demo.getDemoZoneVisitHistory(id);
  if (url.includes('/high-risk-zones') && url.includes('/stats')) return demo.getDemoZoneVisitStats(id);
  if (url.includes('/high-risk-zones') && url.includes('/map')) return demo.getDemoZoneVisitMap(id);
  if (url.includes('/high-risk-zones')) return demo.getDemoActiveZones();

  // Case Summary
  if (url.includes('/case-summary/') && !url.includes('/analysis')) return demo.getDemoCaseSummary(id);

  // People/defendants list (for person dropdown)
  if (url.includes('people/aliens')) return demo.getDemoPeopleList();
  if (url.includes('/Bail/defendants') || url.includes('/bail/defendants')) {
    return { items: demo.getDemoPeopleList(), totalCount: 3, page: 1, pageSize: 10000, totalPages: 1 };
  }

  // Person info lookups (for network panel)
  if (url.includes('/people/by-userids') && method === 'post') {
    try {
      const ids = config.data ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) : [];
      return demo.getDemoPersonBatch(Array.isArray(ids) ? ids : []);
    } catch {
      return demo.getDemoPersonBatch([]);
    }
  }
  if (url.includes('/people/') && url.includes('/info')) return demo.getDemoPersonInfo(id);

  // Network/anomaly endpoints that may use mainApiClient
  if (url.includes('/train-ensemble') || url.includes('/TrainAnomalyEnsemble')) {
    return demo.getDemoTrainEnsemble(id);
  }

  return undefined; // Not matched — let real request through
}

function matchMlFunctionsUrl(url, config) {
  if (!url) return undefined;
  const id = extractId(url);

  if (url.includes('/GetMovementSummary')) return demo.getDemoMovementSummary(id);
  if (url.includes('/GetGPSQualityStats')) return demo.getDemoGPSQualityStats(id);
  if (url.includes('/GetSmartGeofences')) return demo.getDemoSmartGeofences(id);
  if (url.includes('/CheckGeofenceCompliance')) return demo.getDemoGeofenceCompliance(id);
  if (url.includes('/GetSpoofHistory')) return demo.getDemoSpoofHistory(id);
  if (url.includes('/CheckSpoofing')) return demo.getDemoCheckSpoofing(id);
  if (url.includes('/GetRouteHistory')) return demo.getDemoRouteHistory(id);
  if (url.includes('/CheckVelocityThreshold')) return demo.getDemoVelocityThreshold(id);
  if (url.includes('/BuildNetworkGraph')) return demo.getDemoNetworkBuildResult();
  if (url.includes('/GetNetworkAnalysis')) return demo.getDemoNetworkAnalysis(id);
  if (url.includes('/GetNetworkVisualization')) return demo.getDemoNetworkVisualization(id);
  if (url.includes('/CalculateAssociationRisk')) return demo.getDemoAssociationRisk(id);
  if (url.includes('/GetConnectedPersons')) return demo.getDemoNetworkAnalysis(id).connectedPersons;
  if (url.includes('/GetCommunityMembers')) return demo.getDemoCommunityMembers(id);
  if (url.includes('/GetEdgeDetails')) return demo.getDemoEdgeDetails(id, id);
  if (url.includes('/BuildPatternOfLife')) return demo.getDemoBuildPOL(id);
  if (url.includes('/TrainAnomalyEnsemble')) return demo.getDemoTrainEnsemble(id);
  if (url.includes('/PredictAnomaly') || url.includes('/DetectAnomaly')) return demo.getDemoDetectAnomaly(id);
  if (url.includes('/GetHeatmap')) return demo.getDemoHeatmapData(id);
  if (url.includes('/GetBehavioralHeatmap')) return demo.getDemoHeatmapData(id);
  if (url.includes('/GetTemporalHeatmap')) return demo.getDemoHeatmapData(id);
  if (url.includes('/GetClusterAssignment')) return demo.getDemoClusterAssignment(id);

  // Batch GPS processing
  if (url.includes('/ProcessGPSBatch') || url.includes('/ProcessGPSPoint')) {
    return {
      totalPoints: 5,
      validPoints: 5,
      rejectedPoints: 0,
      averageQuality: 0.85,
      processedPoints: [],
    };
  }
  if (url.includes('/GetLastProcessedPoint')) {
    return {
      personId: Number(id),
      qualityScore: 0.88,
      spoofRisk: 0.05,
      velocityMph: 0,
      accuracyMeters: 8.3,
      rawLatitude: 27.9506,
      rawLongitude: -82.4572,
      processedLatitude: 27.9506,
      processedLongitude: -82.4572,
      warnings: [],
    };
  }

  return undefined;
}
